/* eslint prefer-promise-reject-errors: 0 */
/* eslint no-nested-ternary: 0 */
import axios from 'axios';
import { DEFAULT_PORT_SOURCE, getAgentPorts, getNewMessage, getNewState, getPorts } from '../actions/bloquesAction';
import { uniquePortName, usbIdToHex } from './portIdentity';
import { REASON, matchStagedRead, observePlugEvents, requestBeforeLink, requestSerialNumber, resolveBeforeLink, resolveFromGrants, resolveOnPlug } from './usbSerialNumber';
import { getBuildTarget } from './buildTargets';
import AvrgirlArduino from './avrgirl/avrgirl-arduino-browser';
import { getSerialSession } from './traceability/serialSession';
import MonitorFeed from './traceability/monitorFeed';
import { createFirmwareNoiseFilter } from './traceability/firmwareNoise';

// Quién pide el puerto compartido cuando el monitor serie esta abierto. La sesión
// lleva la cuenta por dueño, así que el ping de trazabilidad y el monitor pueden
// tenerlo tomado a la vez y el puerto se cierra recién cuando se van los dos.
const MONITOR_OWNER = 'serial-monitor';

/**
 * How long serial number reads are held off once an upload starts. A board being
 * flashed re-enumerates: the avr109 bootloader comes up under a different product
 * id, so one upload produces a disconnect and a connect of the bootloader and then
 * of the sketch again. Pairing a plug in that window could tie the arrival of the
 * bootloader to the port of the sketch.
 *
 * A deadline and not a flag: a flash that never reports back would leave a flag
 * set and switch serial reads off for the rest of the session.
 */
const FLASH_QUIET_MS = 30000;

/**
 * Cuánto se retiene el evento de conexión de una placa esperando a que se sepa
 * su número.
 *
 * Largo porque el camino más lento es legítimo y tiene a una persona adentro:
 * instalar el firmware es compilar, grabar y reiniciar, y antes de eso el usuario
 * tiene que leer un modal y apretar un botón. Cortar en el medio de eso reportaría
 * sin número a una placa que estaba por tenerlo.
 *
 * Y existe porque nadie puede quedar esperando para siempre: si el que tenía que
 * contestar no está -- la pantalla se desmontó, algo se colgó --, la placa se
 * reporta igual.
 */
const IDENTITY_HOLD_MS = 120000;

class WebSerialAgent {
  constructor(store, board, socketElectronConnection, httpElectronConnection, serialMonitorMessages, intl, portSource = DEFAULT_PORT_SOURCE, { sharedPort = false, identity = false } = {}) {
    this.store = store;
    // This instance only sees the ports of its own transport, so it publishes them
    // under its own source and the reducer merges them with the other agents'.
    this.portSource = portSource;
    this.board = board;
    this.socketElectronConnection = socketElectronConnection;
    this.httpElectronConnection = httpElectronConnection;
    this.selectedPort = null;
    this.monitor = '';
    this.portList = [];
    this.serialPortList = [];
    // The port the user picked in the chooser last. The instance is the anchor:
    // WebSerial hands back the same SerialPort object for the same physical port.
    this.lastLinkedPort = null;
    /**
     * What the serial number read came back with, waiting for the port it belongs
     * to. The read runs before the board is linked, so it lands before there is
     * anything to write it on, and the port chooser is narrowed with the ids of
     * whatever it identified.
     *
     * Only an explicit link consumes it. A board arriving on its own, or a fresh
     * enumeration, must not pick up a read the user made pointing at another one.
     */
    this.stagedRead = null;
    /**
     * Con esto en true el puerto lo maneja serialSession y el monitor es un
     * suscriptor más, en lugar de abrirlo para él solo. Es lo que deja que el ping
     * del protocolo Educabot conviva con el monitor y con el flasheo.
     */
    this.sharedPort = sharedPort;
    this.monitorSession = null;
    this.monitorSubscription = null;
    this.monitorFeed = null;
    /**
     * Con esto en true la identidad de cada placa la resuelve BoardIdentity sobre
     * el puerto ya vinculado, y el evento de conexión espera a que conteste.
     */
    this.identity = identity;
    // Puerto -> el plazo que lo suelta si la identidad no llega. Van aparte de la
    // entrada porque esa lista termina en redux.
    this.identityHolds = new Map();
    const compilerBoardName = this.board.compilerBoard.split(':');
    const compilerBoard = compilerBoardName[2] || null;
    this.intl = intl;

    // console.log('==================', compilerBoard, board);
    this.avrgirl = null;
    try {
      this.avrgirl = new AvrgirlArduino({
        board: compilerBoard,
        debug: true,
      });
      this.avrgirl.connection.serialPort.on('connect', (port) => {
        // console.log('======================CONNECT', port);
        this.addPort(port, { fromPlug: true });
      });
      this.avrgirl.connection.serialPort.on('disconnect', (port) => {
        // console.log('======================DISCONNECT', port);
        this.removePort(port);
      });
    } catch (error) {
      this.store.dispatch(getNewMessage(error.toString()));
    }

    if (process.env.IS_ELECTRON || 'serial' in navigator) {
      this.store.dispatch(getNewState('#### PORT CONNECTED'));
      this.store.dispatch(getNewState('#### BOARD NOT CONNECTED'));
    }

    // Started before any board arrives: pairing a plug needs both of its events,
    // and the usb one is missed if nothing was listening when it fired.
    observePlugEvents();

    this.serialMonitorMessages = serialMonitorMessages;
    this.findInitialPorts();
  }

  disconnectSocket = () => {}

  stopCheckingAgent = () => { }

  /**
   * The names the selector shows. Sent as soon as a board is listed, so it can be
   * picked right away, while the traceability list waits for its serial number.
   *
   * Derived from serialPortList instead of being kept in sync by index: both arrays
   * used to be spliced with the same index, which only holds while every push
   * happens in the same tick.
   */
  publishPortNames = ({ announceState = true } = {}) => {
    this.portList = this.serialPortList.map((p) => p.Name);
    this.store.dispatch(getPorts(this.portList, this.portSource));
    if (!announceState) return;
    if (this.portList.length > 0) {
      this.store.dispatch(getNewState('#### BOARD CONNECTED'));
    } else this.store.dispatch(getNewState('#### BOARD NOT CONNECTED'));
  }

  /**
   * The full list, which is what the connection events are diffed from. Sent once
   * the serial number of a new board has been read or given up on, so the board is
   * reported a single time with its identity already resolved.
   */
  publishPorts = ({ announceState = true } = {}) => {
    this.publishPortNames({ announceState });
    this.store.dispatch(getAgentPorts(this.serialPortList, this.portSource));
  }

  findEntryByPort = (port) => (port ? this.serialPortList.find((p) => p.SerialPort === port) : null) || null

  // Listed ports of one model, which is what tells a read resolved without asking
  // apart from one that cannot be tied to a single board.
  countPortsWithIds = (vendorID, productID) => this.serialPortList.filter((p) => p.VendorID === vendorID && p.ProductID === productID).length

  /**
   * Ties the staged read to a port, and clears it either way: one that missed its
   * port is spent, and leaving it there would hand it to the next board instead.
   */
  consumeStagedRead = (vendorID, productID) => {
    const staged = this.stagedRead;
    this.stagedRead = null;
    if (!staged) return null;
    return matchStagedRead(staged, { vendorID, productID, sameIdPortCount: this.countPortsWithIds(vendorID, productID) });
  }

  /**
   * Writes the staged read on a board that was already listed when the read was
   * made, which is the case when the page opened with it plugged in. Its CONNECTED
   * went out before the serial could be read, so this one travels as a correction
   * of that event rather than as part of it.
   */
  applyStagedRead = (port) => {
    const entry = this.findEntryByPort(port);
    if (!entry) return;
    const result = this.consumeStagedRead(entry.VendorID, entry.ProductID);
    if (result) this.applySerialNumber(port, result);
  }

  /**
   * Places the staged read on a board that is already listed, when the read could
   * not be aimed at one to begin with: more than one port is listed and none was
   * linked in this session, so there was no entry to read against. The ids of the
   * device are what place it, and only a single listed port carrying them is
   * placed on - two of the same model cannot be told apart.
   *
   * A board reached this way had its connection reported before the read, so the
   * serial travels as a correction of that event.
   */
  applyStagedReadToListedPort = () => {
    const staged = this.stagedRead;
    if (!staged || !staged.serialNumber) return;
    const matches = this.serialPortList.filter((p) => matchStagedRead(staged, { vendorID: p.VendorID, productID: p.ProductID }).serialNumber);
    if (matches.length !== 1) return;
    this.stagedRead = null;
    this.applySerialNumber(matches[0].SerialPort, staged);
  }

  /**
   * Narrows the WebSerial chooser to the board the read identified, so the two
   * choosers of the flow cannot be answered with different boards. Undefined when
   * nothing was identified, which leaves the chooser as it was.
   */
  serialRequestOptions = () => {
    const staged = this.stagedRead;
    if (!staged || staged.vendorId == null || staged.productId == null) return undefined;
    return { filters: [{ usbVendorId: staged.vendorId, usbProductId: staged.productId }] };
  }

  // See FLASH_QUIET_MS.
  holdSerialReads = () => { this.serialReadsHeldUntil = Date.now() + FLASH_QUIET_MS; }

  serialReadsHeld = () => Date.now() < (this.serialReadsHeldUntil || 0)

  /**
   * Writes what was read onto the listed port, publishing nothing. An empty read
   * is an ordinary outcome: an older board carries no serial in its descriptor,
   * and one that has never been through the WebUSB chooser cannot be read without
   * it.
   *
   * The port is looked up again rather than held on to, because the read takes
   * long enough for the board to have been unplugged in the meantime, and writing
   * onto an entry already spliced out of the list would go nowhere.
   */
  writeSerialNumber = (port, result) => {
    if (!port || !result) return false;
    const index = this.serialPortList.findIndex((p) => p.SerialPort === port);
    if (index < 0) return false;
    /**
     * Written even when nothing was read, because why a board has no serial is
     * what the event carries as serial_reason, and it is the only thing that tells
     * a browser which faked the read apart from a board that carries none.
     *
     * Never written over the reason of a serial already on the entry: a later
     * empty read would otherwise replace 'none' with the reason of a read that
     * changed nothing.
     */
    if (!this.serialPortList[index].UsbSerialNumber || result.serialNumber) {
      this.serialPortList[index].SerialReason = result.reason;
    }
    if (!result.serialNumber) return false;
    if (this.serialPortList[index].UsbSerialNumber === result.serialNumber) return true;
    this.serialPortList[index].UsbSerialNumber = result.serialNumber;
    this.serialPortList[index].SerialConfidence = result.confidence;
    this.serialPortList[index].SerialMatch = result.match;
    return true;
  }

  /**
   * Writes it and republishes, for a read that landed after the board had already
   * been reported, which is what the modal produces. No connection event comes out
   * of it: the diff keys ports by name, so nothing there changed. The serial
   * travels with the next event of that board instead, which right after the modal
   * is the CODE_BUILD of the upload that opened it.
   */
  applySerialNumber = (port, result) => {
    const written = this.writeSerialNumber(port, result);
    if (written) this.publishPorts({ announceState: false });
    return written;
  }

  removePort = (port) => {
    this.clearIdentityHold(port);
    for (let i = 0; i < this.serialPortList.length; i += 1) {
      if (this.serialPortList[i].SerialPort === port) {
        this.serialPortList.splice(i, 1);
        this.publishPorts();
        break;
      }
    }
  }

  addPort = (port, { fromPlug = false, linked = false } = {}) => {
    let exists = false;
    this.serialPortList.map((p) => {
      if (p.SerialPort === port) {
        exists = true;
      }
      return true;
    });
    if (!exists) {
      const portInfo = port.getInfo();
      // console.log('==================portInfo', portInfo);
      const vendorID = usbIdToHex(portInfo.usbVendorId);
      const productID = usbIdToHex(portInfo.usbProductId);
      const name = uniquePortName(`Puerto (${vendorID}-${productID})`, this.serialPortList);

      this.serialPortList.push({
        Name: name,
        SerialPort: port,
        VendorID: vendorID,
        ProductID: productID,
      });
      // The board shows up in the selector now; the connection event waits below.
      this.publishPortNames();

      /**
       * La identidad la resuelve BoardIdentity sobre el puerto ya vinculado, así
       * que acá no se lee nada por WebUSB: se retiene el reporte y se espera.
       */
      if (this.identity) {
        this.holdIdentity(port);
        this.publishPorts({ announceState: false });
        return;
      }

      if (this.serialReadsHeld()) {
        this.publishPorts();
        return;
      }
      // The serial was read before this board was linked, so it goes on the entry
      // before the list is published and the board is reported once, identified.
      if (linked && this.stagedRead) {
        this.writeSerialNumber(port, this.consumeStagedRead(vendorID, productID));
        this.publishPorts({ announceState: false });
        return;
      }
      /**
       * The full list is held back until the read settles, so the board is reported
       * once, already identified, instead of being reported anonymous and corrected
       * afterwards. Both reads always settle - one answers straight away and the
       * other has its own deadline - and the catch covers the rest, because a list
       * never published would be a board never reported.
       */
      const read = fromPlug
        ? resolveOnPlug({ vendorID, productID })
        : resolveFromGrants({ vendorID, productID });
      read
        .then((result) => this.writeSerialNumber(port, result))
        .catch(() => {})
        .then(() => this.publishPorts({ announceState: false }));
    } else if (linked) {
      this.applyStagedRead(port);
    }
  }

  /**
   * Retiene el reporte de una placa hasta que se sepa con qué número se
   * identifica. La placa se lista igual -- aparece en el selector y se le puede
   * mandar código --; lo único que espera es el evento de conexión.
   *
   * Ver pendingIdentity en boardConnectionInfo: el serial es con lo que se agrupa
   * el uso, así que conviene reportar la placa una sola vez y ya identificada.
   */
  holdIdentity = (port) => {
    const entry = this.findEntryByPort(port);
    if (!entry || this.identityHolds.has(port)) return;
    entry.IdentityPending = true;
    this.identityHolds.set(port, setTimeout(() => this.releaseIdentityHold(port), IDENTITY_HOLD_MS));
  }

  clearIdentityHold = (port) => {
    const timer = this.identityHolds.get(port);
    if (timer) clearTimeout(timer);
    this.identityHolds.delete(port);
  }

  /**
   * Se acabó la espera y la identidad no llegó. La placa se reporta igual, sin
   * número: una placa sin identificar en los datos es peor que nada, pero una
   * conexión que nunca se reportó es peor todavía. Si el número aparece después,
   * viaja como corrección de ese mismo evento.
   */
  releaseIdentityHold = (port) => {
    this.clearIdentityHold(port);
    const entry = this.findEntryByPort(port);
    if (!entry || !entry.IdentityPending) return;
    entry.IdentityPending = false;
    this.publishPorts({ announceState: false });
  }

  /**
   * Lo que averiguó la máquina de identidad sobre esta placa. Es lo que suelta el
   * evento de conexión retenido, con el número ya adentro.
   *
   * Se escribe aunque no haya número: el serial de hardware sirve igual, y sin
   * soltar la retención la placa no se reportaría hasta que venza el plazo.
   */
  setBoardIdentity = (port, { serialString = null, hardwareSerial = null, source = null } = {}) => {
    const entry = this.findEntryByPort(port);
    if (!entry) return false;
    this.clearIdentityHold(port);
    if (hardwareSerial) entry.HardwareSerial = hardwareSerial;
    if (serialString) {
      entry.SerialString = serialString;
      entry.SerialSource = source;
      entry.SerialReason = REASON.NONE;
    }
    entry.IdentityPending = false;
    this.publishPorts({ announceState: false });
    return true;
  }

  /**
   * El serial que el chip usb declara en su descriptor, para la máquina de
   * identidad. Es el camino de las pocas placas entregadas con el número grabado
   * en el ch340 y nada en la EEPROM.
   *
   * `prompt` es la diferencia entre pedirle el permiso al usuario -- abre el
   * selector, necesita venir de un click -- y leerlo de los permisos ya dados, que
   * no abre nada. La segunda forma es la que hace que esas placas molesten una
   * sola vez por navegador.
   *
   * Lo que se lee pasa por el mismo chequeo de siempre antes de devolverse: un
   * serial pegado a la placa equivocada corrompe la historia de dos placas a la
   * vez, y eso no lo arregla nadie después.
   */
  readUsbSerialFor = async (port, { prompt = false } = {}) => {
    const entry = this.findEntryByPort(port);
    if (!entry) return null;
    const result = prompt
      ? await requestSerialNumber({ vendorID: entry.VendorID, productID: entry.ProductID })
      : await resolveFromGrants({ vendorID: entry.VendorID, productID: entry.ProductID });
    const matched = matchStagedRead(result, {
      vendorID: entry.VendorID,
      productID: entry.ProductID,
      sameIdPortCount: this.countPortsWithIds(entry.VendorID, entry.ProductID),
    });
    return matched.serialNumber || null;
  }

  /**
   * Reads the serial number of the board about to be linked. Runs from a click,
   * which WebUSB requires to open its chooser, and comes before the link so that
   * the port can be listed with its identity already in hand.
   *
   * Answers with a REASON rather than a plain yes or no, because the step has to
   * tell a dismissed chooser apart from a board whose descriptor has no serial:
   * the first is the one worth offering again, and the second never will be. That
   * is what lets the step be mandatory without walling in an older board.
   *
   * A board that is already listed is read the other way round, against the ids of
   * its port: that happens when the page opened with it plugged in, and when it
   * already carries a serial there is nothing to ask for at all.
   */
  fillBoardSerialNumber = async (port = this.lastLinkedPort, { unfiltered = false } = {}) => {
    const entry = this.findEntryByPort(port) || ((this.serialPortList.length === 1) ? this.serialPortList[0] : null);
    if (entry) {
      if (entry.UsbSerialNumber) return REASON.NONE;
      // Narrowed to the ids of the port itself, which cannot hide the board being
      // read. Lifting that leaves the chooser offering everything, so what comes
      // back goes through the same check as a read taken before linking instead of
      // being written on the strength of the filter alone.
      const result = unfiltered
        ? await requestBeforeLink({ unfiltered: true })
        : await requestSerialNumber({ vendorID: entry.VendorID, productID: entry.ProductID });
      const matched = matchStagedRead(result, {
        vendorID: entry.VendorID,
        productID: entry.ProductID,
        sameIdPortCount: this.countPortsWithIds(entry.VendorID, entry.ProductID),
      });
      this.applySerialNumber(entry.SerialPort, matched);
      // A serial that was read but belongs to something else leaves the step where
      // a dismissed chooser does: nothing was written, and another go is what the
      // user needs. Saying it was read would be a success with nothing behind it.
      if (result.reason === REASON.NONE && !matched.serialNumber) return REASON.UNAVAILABLE;
      return result.reason;
    }
    // Answered from the grants when it can be, so a board goes through the chooser
    // once per browser and is recognised on its own from then on. Asking again
    // after a failed read costs one query and answers the same way, unless the
    // boards on the machine changed in between - in which case the answer is right.
    let result = await resolveBeforeLink();
    if (result.reason === REASON.UNAVAILABLE) result = await requestBeforeLink({ unfiltered });
    this.stagedRead = (result.reason === REASON.UNAVAILABLE) ? null : result;
    this.applyStagedReadToListedPort();
    return result.reason;
  }

  getSerialPortByName = (name = '') => {
    let serialPort = null;
    for (let i = 0; i < this.serialPortList.length; i += 1) {
      if (this.serialPortList[i].Name === name) {
        serialPort = this.serialPortList[i].SerialPort;
        break;
      }
    }

    return serialPort;
  }

  connectNewBoard = () => {
    if (this.avrgirl) {
      this.avrgirl.connectNewBoard((error, port) => {
        if (error) {
          // console.log('==================new ports error', error, port);
          // this.store.dispatch(getNewMessage(error.toString()));
          if (this.serialPortList.length === 0) {
            this.store.dispatch(getNewMessage(this.intl.formatMessage({ id: 'agent.boardNotSelected' })));
            this.store.dispatch(getNewState('#### BOARD NOT CONNECTED'));
          }
        } else {
          this.lastLinkedPort = port;
          this.addPort(port, { linked: true });
        }
        // console.log('==================new port', port);
      }, this.serialRequestOptions());
    }
  }

  findPorts = () => {}

  findInitialPorts = () => {
    if (this.avrgirl) {
      this.avrgirl.findPorts((error, ports) => {
        if (error) {
          // console.log('==================ports error', error);
          this.store.dispatch(getNewMessage(error.toString()));
          return;
        }
        for (let i = 0; i < ports.length; i += 1) {
          this.addPort(ports[i]);
        }
      });
    }
  }

  /**
   * El flasheo necesita el puerto para él solo: avrgirl lo abre por su cuenta, a
   * otra velocidad, y resetea la placa con DTR. Con el puerto compartido se lo
   * pedimos prestado a la sesión, que lo cierra antes y lo vuelve a abrir después
   * sin que el monitor ni el ping tengan que enterarse.
   *
   * El callback de avrgirl se mantiene tal cual para no tocar a quien llama: lo
   * único que agrega la promesa es saber cuándo soltar el préstamo.
   */
  flashAvr = (serialPort, hexBuffer, callback) => {
    const run = () => new Promise((resolve) => {
      this.avrgirl.flash(serialPort, hexBuffer, (error) => {
        callback(error);
        resolve();
      });
    });
    if (this.sharedPort && serialPort) {
      return getSerialSession(serialPort).withExclusiveAccess(run);
    }
    return run();
  }

  /**
   * El monitor sobre el puerto compartido: en vez de abrirlo, se anota en la
   * sesión y escucha la salida de texto, que ya viene sin las tramas del
   * protocolo. Es lo mismo que hace el agente ESP.
   */
  openSharedMonitor = async (serialPort, baudios) => {
    const session = getSerialSession(serialPort, { baudRate: baudios });
    /**
     * El puerto ya abierto conserva su velocidad: el protocolo Educabot corre a
     * 9600 fijos y reabrir a otra velocidad dejaría de entender las tramas. Para
     * estas placas es lo mismo de todos modos -- el sketch abre a 9600 --, pero el
     * selector de baudios del monitor sigue mostrando lo que eligió el usuario.
     */
    if (session.isOpen && session.baudRate !== baudios) {
      console.log(`El monitor queda a ${session.baudRate} baudios: el puerto ya estaba abierto a esa velocidad.`); // eslint-disable-line no-console
    }
    this.monitorSession = session;
    if (!this.monitorSubscription) {
      // Amortiguado: ver MonitorFeed. Sin esto, un sketch que imprime sin delay
      // deja la pestaña sin main thread y el monitor no llega ni a abrirse.
      this.monitorFeed = new MonitorFeed((entry) => this.serialMonitorMessages.next(entry));
      /**
       * La sesión ya sacó las tramas binarias, pero la librería de trazabilidad
       * además imprime diagnósticos en texto plano, que por el protocolo son
       * indistinguibles de lo que escribe el usuario. Ver firmwareNoise.
       */
      const dropFirmwareNoise = createFirmwareNoiseFilter();
      this.monitorSubscription = session.text.subscribe((text) => this.monitorFeed.push(dropFirmwareNoise(text)));
    }
    try {
      await session.acquire(MONITOR_OWNER);
    } catch (error) {
      this.store.dispatch(getNewMessage(`Error al abrir monitor: ${error.message || error}`));
    }
  }

  closeSharedMonitor = async () => {
    if (this.monitorSubscription) {
      this.monitorSubscription.unsubscribe();
      this.monitorSubscription = null;
    }
    if (this.monitorFeed) {
      this.monitorFeed.stop();
      this.monitorFeed = null;
    }
    this.monitor = '';
    const session = this.monitorSession;
    this.monitorSession = null;
    if (session) await session.release(MONITOR_OWNER);
  }

  openMonitor = (port = '', baudios = 9600) => {
    if (this.sharedPort) {
      const serialPort = this.getSerialPortByName(port);
      if (serialPort) this.openSharedMonitor(serialPort, baudios);
      return;
    }
    if (this.avrgirl) {
      const serialPort = this.getSerialPortByName(port);
      // console.log('==============', port, serialPort);
      this.avrgirl.openMonitor(serialPort, baudios, (error, message) => {
        if (error) {
          this.store.dispatch(getNewMessage(error.toString()));
          return;
        }
        this.monitor += message;
        if (message.match(/\n/g) || this.monitor.length > 100) {
          const date = new Date();
          const time = `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}:${String(date.getSeconds()).padStart(2, '0')}.${String(date.getMilliseconds()).padStart(3, '0')}`;
          this.serialMonitorMessages.next({ time, message: this.monitor });
          this.monitor = '';
        }
      });
    }
  }

  closeMonitor = (port = '') => {
    if (this.sharedPort) {
      this.closeSharedMonitor();
      return;
    }
    if (this.avrgirl) {
      const serialPort = this.getSerialPortByName(port);
      this.avrgirl.closeMonitor(serialPort, () => { });
    }
  }

  sendMonitor = (port = '', command = '') => {
    // Con el puerto compartido el writer es de la sesión: pedir otro acá chocaría
    // con el lock que ya tiene tomado.
    if (this.sharedPort) {
      const serialPort = this.getSerialPortByName(port);
      if (serialPort) getSerialSession(serialPort).write(command);
      return;
    }
    if (this.avrgirl) {
      this.avrgirl.send(command, (error) => {
        if (error) this.store.dispatch(getNewMessage(error.toString()));
      });
    }
  };

  // changeBaudios = (port = '', baudios = 0) => { };

  uploadToPort = (code = '', board = '', port = '') => {
    if (this.avrgirl) {
      this.holdSerialReads();
      const serialPort = this.getSerialPortByName(port);
      // this.store.dispatch(getNewState('#### BOARD CONNECTED'));
      this.store.dispatch(getNewMessage(this.intl.formatMessage({ id: 'agent.codeIsUploading' })));
      this.store.dispatch(getNewState('#### UPLOAD TO PORT'));

      const selectedBoard = board.compilerBoard || 'arduino';

      /**
       * Una sola fuente para los tres builders, la misma que usa el agente ESP.
       * Acá había una copia con su propio fallback, y las dos se habían ido
       * separando: una misma sesión de desarrollo compilaba las placas AVR contra
       * staging y las ESP contra producción.
       */
      const { uri: buildURI, params: buildParams } = getBuildTarget(selectedBoard, code, this.httpElectronConnection);
      if (selectedBoard === 'microbit') {
        this.store.dispatch(getNewState('#### UPLOAD TO EB_BUILD'));
      }
      this.store.dispatch(getNewState('#### UPLOAD TO EB_BUILD'));
      axios.post(
        buildURI,
        buildParams,
        {
          withCredentials: false,
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'Accept': '*/*', //eslint-disable-line
          },
        },
      ).then((response) => {
        if (response.data.status === 'SUCCESS' && response.data.payload.hex) {
          this.store.dispatch(getNewState('#### FINISH EB_BUILD'));
          this.store.dispatch(getNewState('#### UPLOAD TO AGENT'));

          if (selectedBoard === 'microbit') {
            // MICROBIT BUILDER
            const blob = new Blob([atob(response.data.payload.hex)], { type: 'octet/stream' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.target = '_blank';
            a.download = 'microbit.hex';
            a.setAttribute('id', 'clickme');
            document.body.appendChild(a);
            a.click();

            this.store.dispatch(getNewState('#### FINISH AGENT'));
          } else {
            const hexBuffer = Buffer.from(atob(response.data.payload.hex));
            this.flashAvr(serialPort, hexBuffer, error => {
              if (error) {
                this.store.dispatch(getNewState('#### ERROR EB_BUILD'));
                this.store.dispatch(getNewMessage(error.toString()));
              } else {
                // this.store.dispatch(getNewState('#### FINISH EB_BUILD'));
                this.store.dispatch(getNewState('#### FINISH AGENT'));
              }
            });
          }
        } else {
          this.store.dispatch(getNewState('#### ERROR EB_BUILD'));
          this.store.dispatch(getNewMessage(response.data.message));
        }
      }).catch((responseError) => {
        this.store.dispatch(getNewState('#### ERROR EB_BUILD'));
      });
    }
  }

  uploadRemoteSession = (hex = '', board = '', port = '') => new Promise((resolve, reject) => {
    if (this.avrgirl) {
      this.holdSerialReads();
      const serialPort = this.getSerialPortByName(port);
      this.store.dispatch(getNewState('#### UPLOAD TO PORT'));
      /**
       * Response object
       * status: Possible values are SUCCESS, ERROR.
       * message: Error message, if there is any.
       */
      const selectedBoard = board.compilerBoard || 'arduino';
      if (selectedBoard !== 'microbit' && (!port || port === '')) {
        this.store.dispatch(getNewState('#### BOARD NOT CONNECTED'));
        this.store.dispatch(getNewMessage(this.intl.formatMessage({ id: 'agent.boardNotConnected' })));
        reject(this.intl.formatMessage({ id: 'agent.boardNotConnected' }));
      }
      this.store.dispatch(getNewState('#### BOARD CONNECTED'));
      if (!board) {
        this.store.dispatch(getNewMessage(this.intl.formatMessage({ id: 'agent.boardMustBeSelected' })));
        reject('Debes seleccionar una placa');
      }

      this.store.dispatch(getNewMessage(this.intl.formatMessage({ id: 'agent.remoteCodeIsUploading' })));

      this.store.dispatch(getNewState('#### UPLOAD TO EB_BUILD'));

      /* eslint-disable */
      if (selectedBoard === 'microbit') {
        this.store.dispatch(getNewState('#### FINISH AGENT'));
        // MICROBIT BUILDER
        const blob = new Blob([atob(hex)], { type: 'octet/stream' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.target = '_blank';
        a.download = 'microbit.hex';
        a.setAttribute('id', 'clickme');
        document.body.appendChild(a);
        a.click();
      } else {
        const hexBuffer = Buffer.from(atob(hex));
        this.flashAvr(serialPort, hexBuffer, error => {
          if (error) {
            this.store.dispatch(getNewState('#### ERROR EB_BUILD'));
            this.store.dispatch(getNewMessage(error.toString()));
            reject(error.toString());
          } else {
            // this.store.dispatch(getNewState('#### FINISH EB_BUILD'));
            this.store.dispatch(getNewState('#### FINISH AGENT'));
            resolve('OK');
          }
        });
      }
    } else {
      reject('Placa no soportada');
    }
  });
}

export default WebSerialAgent;
